import { useGetProposal } from '@urbanx/agx.proposals.client';
import { DocumentTypes } from '@urbanx/agx.campaigns.client';
import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Feature, useGetFeatureToggles } from '@urbanx/agx.agencies.client';
import { FormType, useLoadForm } from '@urbanx/agx.campaigns.client';
import { AustralianState, FormConfigType } from '@urbanx/agx-ui-components';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { clearForm, loadFormConfig, setSelectedForm } from './formReducer';

export const FormEntry = ({ children }: { children: ReactNode }) => {
  const [params] = useSearchParams();
  const dispatch = useAppDispatch();
  const [initializedNewDraft, setInitializedNewDraft] = useState(false);
  const form = useAppSelector((state) => state.form);
  const agencyStates = useAppSelector((state) => state.agencies.agencyInfo.info.agencyStates);
  const campaignId = params.get('campaignId');
  const formId = params.get('formId');
  const newProposal = params.get('formType') === FormType.ListingProposal;
  const state = [AustralianState.NSW, AustralianState.QLD].find(
    (state) => state === params.get('state')
  );
  const loaded = campaignId === form.campaignId && formId === form.formId;
  const query = useLoadForm(campaignId ?? '', formId ?? '', !!campaignId && !!formId && !loaded);
  const toggles = useGetFeatureToggles();
  const isProposal =
    newProposal ||
    query.data?.formType === FormType.ListingProposal ||
    (loaded && form.selectedForm?.formType === FormType.ListingProposal);
  const enabled = toggles.data?.enabledFeatures?.includes(Feature.Proposals);
  const allowed = !isProposal || (!toggles.isPending && !toggles.isError && enabled);
  const supportedState =
    state && agencyStates?.some((agencyState: string | null) => agencyState === state);

  useEffect(() => {
    if (!allowed) return;
    if (query.data && !loaded) {
      dispatch(
        loadFormConfig({
          ...query.data,
          state: AustralianState[query.data.state],
          pageStatuses: query.data.pageStatuses ?? [],
          documents: query.data.documents.map((document) => ({
            ...document,
            documentType: DocumentTypes[document.documentType],
          })),
          formConfigType: FormConfigType.Campaign,
        })
      );
    } else if (
      newProposal &&
      !campaignId &&
      !formId &&
      state &&
      supportedState &&
      !initializedNewDraft
    ) {
      dispatch(clearForm());
      dispatch(
        setSelectedForm({
          formType: FormType.ListingProposal,
          state,
          formConfigType: FormConfigType.Campaign,
        })
      );
      setInitializedNewDraft(true);
    }
  }, [
    allowed,
    query.data,
    loaded,
    dispatch,
    newProposal,
    campaignId,
    formId,
    supportedState,
    state,
    initializedNewDraft,
  ]);

  if ((campaignId && !formId) || (formId && !campaignId)) {
    return <p role="alert">Both campaignId and formId are required to resume a form.</p>;
  }
  if (query.isError) return <p role="alert">Unable to load this form. Reload to retry.</p>;
  if (isProposal) {
    if (toggles.isPending) return <p role="status">Loading proposal access…</p>;
    if (toggles.isError || !toggles.data)
      return <p role="alert">Unable to load feature toggles. Reload to retry.</p>;
    if (!enabled) return <Navigate to="/" replace />;
    if (newProposal && !campaignId) {
      if (!agencyStates) return <p role="status">Loading agency states…</p>;
      if (!supportedState) return <p role="alert">Select a supported NSW or QLD property state.</p>;
      if (
        !initializedNewDraft ||
        form.selectedForm?.formType !== FormType.ListingProposal ||
        form.selectedForm.state !== state
      ) {
        return <p role="status">Loading proposal form…</p>;
      }
    }
  }
  if (campaignId && formId && !loaded) return <p role="status">Loading form…</p>;
  const proposalId = params.get('proposalId');
  if (proposalId) {
    if (!campaignId || !formId || form.selectedForm?.formType !== FormType.ListingProposal)
      return <p role="alert">A proposal return link requires its saved Listing Proposal form.</p>;
    return (
      <ProposalFormReturn proposalId={proposalId} campaignId={campaignId}>
        {children}
      </ProposalFormReturn>
    );
  }
  return children;
};

const ProposalFormReturn = ({
  proposalId,
  campaignId,
  children,
}: {
  proposalId: string;
  campaignId: string;
  children: ReactNode;
}) => {
  const proposal = useGetProposal(proposalId);
  if (proposal.isError)
    return <p role="alert">Unable to load the proposal return link. Reload to retry.</p>;
  if (!proposal.data) return <p role="status">Loading proposal return link…</p>;
  if (proposal.data.subject.campaignId !== campaignId)
    return <p role="alert">This proposal does not belong to the selected campaign.</p>;
  return children;
};
